import { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import Marquee from "react-fast-marquee";
import { Smartphone, Code2, Globe, Share2, TrendingUp, Rocket, ArrowRight, Zap, Users, Award, Headphones } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { TEST_IDS } from "@/constants/testIds";

const ICON_MAP = { Smartphone, Code2, Globe, Share2, TrendingUp, Rocket };

const services = [
  { id: "mobile-app-dev", title: "Mobile App Development", desc: "Native iOS/Android & Cross-platform Flutter/React Native solutions with ultra-fluid 60fps UI/UX.", metrics: "50+ Apps Deployed", icon: "Smartphone" },
  { id: "custom-software-dev", title: "Custom Software Engineering", desc: "Scalable cloud-native architectures, enterprise microservices, API engineering, and automated CI/CD pipelines.", metrics: "99.99% Uptime SLA", icon: "Code2" },
  { id: "web-dev", title: "High-Performance Web Development", desc: "Award-worthy Jamstack, Next.js, and React web applications engineered for lightning SEO and conversions.", metrics: "<0.8s Page Load", icon: "Globe" },
  { id: "social-media-marketing", title: "Social Media Marketing", desc: "Data-driven viral content campaigns, community building, influencer outreach, and brand positioning.", metrics: "+4.5M Impressions", icon: "Share2" },
  { id: "business-development", title: "Strategic Business Development", desc: "B2B growth strategies, funnel optimization, market expansion, and strategic corporate partnership alignment.", metrics: "+340% Revenue Boost", icon: "TrendingUp" },
  { id: "product-marketing", title: "GTM Product Marketing", desc: "End-to-end Go-To-Market launches, positioning statements, user acquisition strategies, and ad performance tuning.", metrics: "15x CAC to LTV", icon: "Rocket" },
];

const techStack = ["React Native", "Flutter", "Next.js", "FastAPI", "Node.js", "AWS", "Python", "Tailwind CSS", "Docker", "Kubernetes", "PostgreSQL", "MongoDB", "TypeScript", "GraphQL", "Figma", "Redis"];

const stats = [
  { label: "Projects Completed", value: 120, suffix: "+", icon: Zap },
  { label: "Client Revenue Generated", value: 45, suffix: "Cr+", prefix: "₹", icon: TrendingUp },
  { label: "Client Satisfaction", value: 98.4, suffix: "%", icon: Award },
  { label: "Support Availability", value: 24, suffix: "/7", icon: Headphones },
];

function AnimatedCounter({ value, suffix = "", prefix = "" }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (!isInView) return;
    const duration = 2000;
    const steps = 60;
    const increment = value / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= value) {
        setCount(value);
        clearInterval(timer);
      } else {
        setCount(Number.isInteger(value) ? Math.floor(current) : parseFloat(current.toFixed(1)));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [isInView, value]);

  return <span ref={ref} className="mono-data text-3xl sm:text-4xl font-bold text-[#00F2FE]">{prefix}{count}{suffix}</span>;
}

export default function HomePage() {
  const heroRef = useRef(null);
  const servicesRef = useRef(null);

  const scrollToServices = () => {
    servicesRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="relative">
      {/* Hero Banner Section */}
      <section data-testid={TEST_IDS.hero.section} ref={heroRef} className="relative w-full overflow-hidden h-[300px] md:h-[500px] lg:h-[800px]" style={{ maxWidth: "1920px", margin: "0 auto" }}>
        {/* Full-bleed background image */}
        <img
          src="https://customer-assets-v7afamib.emergentagent.net/wingman/c56b352e-a1b8-450a-bbd2-f2815af932d7/attachments/848b42720fc24a39bfbb0812f8dbba79_27ef2b54-91a1-4c48-8bd3-dd7f12ab482b.png"
          alt="Devkuba Ventures — We Code Your Vision. We Scale Your Brand."
          className="absolute inset-0 w-full h-full object-cover object-center"
        />

        {/* Buttons — left-aligned with navbar logo, 40px further down */}
        <div className="absolute z-10 left-0 right-0 top-[65%] md:top-[62%] lg:top-[calc(58%+90px)]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              className="flex flex-col gap-[10px] px-4 sm:px-0 sm:flex-row sm:items-start sm:gap-4"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
            >
              <Link to="/quote" className="w-full sm:w-auto">
                <Button
                  data-testid={TEST_IDS.hero.ctaQuote}
                  size="lg"
                  className="w-full sm:w-auto bg-[#F97316] hover:bg-[#EA580C] text-white font-bold px-8 py-6 text-base gap-2 shadow-lg shadow-orange-500/25 transition-colors"
                >
                  Get Instant Quote <ArrowRight className="h-5 w-5" />
                </Button>
              </Link>
              <Button
                data-testid={TEST_IDS.hero.ctaServices}
                size="lg"
                variant="outline"
                onClick={scrollToServices}
                className="w-full sm:w-auto border-2 border-white text-white hover:bg-white/15 px-8 py-6 text-base font-semibold backdrop-blur-sm transition-colors"
              >
                Explore Services
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Marquee Ticker */}
      <section className="py-8 border-y border-white/[0.04] bg-[#060913]">
        <div className="marquee-fade-left">
          <Marquee speed={40} gradient={false} className="overflow-hidden">
            {techStack.map((tech, i) => (
              <span key={i} className="mx-6 text-sm font-mono text-slate-500 tracking-wider whitespace-nowrap">
                {tech} <span className="text-[#00F2FE]/40 ml-6">//</span>
              </span>
            ))}
          </Marquee>
        </div>
      </section>

      {/* Services Grid */}
      <section data-testid={TEST_IDS.services.section} ref={servicesRef} id="services" className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Badge variant="outline" className="mb-4 border-[#0EA5E9]/30 text-[#0EA5E9] bg-[#0EA5E9]/5 px-3 py-1 text-xs tracking-wider uppercase">
              Our Expertise
            </Badge>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-50 mb-4">
              Six Pillars of <span className="gradient-text">Digital Excellence</span>
            </h2>
            <p className="text-slate-400 max-w-xl mx-auto text-sm sm:text-base">
              End-to-end capabilities spanning engineering, design, and growth marketing.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map((svc, i) => {
              const Icon = ICON_MAP[svc.icon];
              return (
                <motion.div
                  key={svc.id}
                  data-testid={TEST_IDS.services.card(svc.id)}
                  className="group relative bg-[#0F172A] border border-white/[0.06] rounded-xl p-6 sm:p-7 transition-all duration-300 hover:border-[#00F2FE]/30 hover:bg-[#0F172A]/80 cursor-default"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                >
                  {/* Glow effect on hover */}
                  <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-[#00F2FE]/5 via-transparent to-[#3B82F6]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                  <div className="relative z-10">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#00F2FE]/10 to-[#3B82F6]/10 border border-[#00F2FE]/20 flex items-center justify-center mb-5">
                      <Icon className="h-6 w-6 text-[#00F2FE]" />
                    </div>
                    <h3 className="font-heading font-semibold text-lg text-slate-100 mb-2.5">{svc.title}</h3>
                    <p className="text-slate-400 text-sm leading-relaxed mb-4">{svc.desc}</p>
                    <div className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                      <span className="text-emerald-400 text-xs font-mono font-medium tracking-wider">{svc.metrics}</span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-[#0A0E1A] border-y border-white/[0.04]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12">
            {stats.map((stat, i) => (
              <motion.div
                key={stat.label}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                <stat.icon className="h-6 w-6 text-[#0EA5E9] mx-auto mb-3 opacity-60" />
                <AnimatedCounter value={stat.value} suffix={stat.suffix} prefix={stat.prefix || ""} />
                <p className="text-slate-500 text-xs sm:text-sm mt-2 tracking-wide">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 sm:py-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-50 mb-6">
              Ready to Build Something <span className="gradient-text">Extraordinary?</span>
            </h2>
            <p className="text-slate-400 max-w-xl mx-auto mb-8 text-sm sm:text-base">
              Get an instant project estimate. Our team will craft a tailored roadmap within 24 hours.
            </p>
            <Link to="/quote">
              <Button
                size="lg"
                className="bg-gradient-to-r from-[#00F2FE] via-[#0EA5E9] to-[#3B82F6] text-[#060913] font-bold hover:opacity-90 transition-opacity px-10 py-6 text-base gap-2"
              >
                Calculate Your Project Cost <ArrowRight className="h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
